import {JsonProperty} from "../shared/json/json-property";
import {JsonObject} from "../shared/json/json-object";
import {md5} from "../shared/md5";
import {SvgIcon} from "@mui/material";
import {$KTM, KeyMetadatas, ObjectConfig, TypedObject} from "../shared/types";
import {
  FormatAlignCenterOutlined,
  FormatAlignLeftOutlined,
  FormatAlignRightOutlined,
  FormatColorFillOutlined,
  ImageOutlined,
  NotesOutlined,
  SettingsOutlined,
  ViewComfyOutlined
} from "@mui/icons-material";
import {Type} from "../shared/json/helpers";

export enum EditorTool {
  IMAGE = "image",
  TEXT = "text",
  LAYOUT = "layout",
  BACKGROUND = "background",
  PREFERENCES = "preferences",
}

export type EditorToolMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const EDITOR_TOOLS_KTM = new KeyMetadatas<EditorToolMetadata>([
  $KTM(EditorTool.IMAGE, {text: "Image", iconType: ImageOutlined}),
  $KTM(EditorTool.TEXT, {text: "Text", iconType: NotesOutlined}),
  $KTM(EditorTool.LAYOUT, {text: "Layout", iconType: ViewComfyOutlined}),
  $KTM(EditorTool.BACKGROUND, {text: "Background", iconType: FormatColorFillOutlined}),
  $KTM(EditorTool.PREFERENCES, {text: "Preferences", iconType: SettingsOutlined}),
]);

export const BG_COLORS: string[] = [
  "#fff",
  "#eee",
  "#ddd",
  "#ccc",
  "alabaster",
];

export const FONT_SIZES: number[] = [
  8, 10, 11, 12, 13, 14, 16, 18, 20, 24,
];

export enum AspectRatio {
  AR_1 = 1,
  AR_3_2 = 3 / 2,
  AR_2 = 2,
  AR_3 = 3,
  AR_3_4 = 3 / 4,
}

export enum PageLayoutBlockType {
  NONE = "none",
  TEXT = "text",
  TEXT_SUBTEXT = "text_subtext",
  IMAGE = "image",
  IMAGE_SUBTEXT = "image_subtext",
}

@JsonObject()
export class PageLayoutBlockSpec {

  @JsonProperty()
  readonly type: PageLayoutBlockType;

  @JsonProperty()
  readonly imageAspectRatio?: AspectRatio;


  static specImage(type: PageLayoutBlockType, imageAspectRatio: AspectRatio) {
    return new PageLayoutBlockSpec(type, imageAspectRatio);
  }

  constructor(type: PageLayoutBlockType, imageAspectRatio?: AspectRatio) {
    this.type = type;
    this.imageAspectRatio = imageAspectRatio;
  }
}

export type PageLayoutTemplate = {
  id: string,
  rows: number,
  cols: number,
  blockSpecs: PageLayoutBlockSpec[],
}

export const PAGE_LAYOUT_TEMPLATES: PageLayoutTemplate[] = [
  {
    id: "image_1_1_ar_1",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_1),
    ],
  },
  {
    id: "image_subtext_1_1_ar_1",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
    ],
  },
  {
    id: "image_subtext_1_2_ar_1",
    rows: 1,
    cols: 2,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
    ],
  },
  {
    id: "image_1_1_ar_3_4",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_3_4),
    ],
  },
  {
    id: "image_subtext_1_1_ar_3_4",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_3_4),
    ],
  },
  {
    id: "image_subtext_1_2_ar_3_4",
    rows: 1,
    cols: 2,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_3_4),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_3_4),
    ],
  },
  {
    id: "image_1_1_ar_3_2",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_3_2),
    ],
  },
  {
    id: "image_subtext_1_1_ar_3_2",
    rows: 1,
    cols: 1,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_3_2),
    ],
  },
  // {
  //   id: "image_2_1_ar_3",
  //   rows: 2,
  //   cols: 1,
  //   blockSpecs: [
  //     PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_3),
  //     PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_3),
  //   ],
  // },
  {
    id: "image_2_2_ar_1",
    rows: 2,
    cols: 2,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE, AspectRatio.AR_1),
    ],
  },
  {
    id: "image_subtext_2_2_ar_1",
    rows: 2,
    cols: 2,
    blockSpecs: [
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
      PageLayoutBlockSpec.specImage(PageLayoutBlockType.IMAGE_SUBTEXT, AspectRatio.AR_1),
    ],
  },
];

export enum PageTransition {
  SLIDE = "slide",
  FADE = "fade",
  CUBE = "cube",
  CARDS = "cards",
  FLIP = "flip",
  COVERFLOW = "coverflow",
  LAYERS = "layers",
}

export type PageTransitionMetadata = {
  text: string,
  swiperEffect: string,
}

export const PAGE_TRANSITION_KTM = new KeyMetadatas<PageTransitionMetadata>([
  $KTM(PageTransition.SLIDE, {text: "Slide", swiperEffect: null}),
  $KTM(PageTransition.FADE, {text: "Fade", swiperEffect: "fade"}),
  $KTM(PageTransition.CUBE, {text: "Cube", swiperEffect: "cube"}),
  $KTM(PageTransition.CARDS, {text: "Cards", swiperEffect: "cards"}),
  $KTM(PageTransition.FLIP, {text: "Flip", swiperEffect: "flip"}),
  $KTM(PageTransition.COVERFLOW, {text: "Coverflow", swiperEffect: "coverflow"}),
  $KTM(PageTransition.LAYERS, {text: "Layers", swiperEffect: "creative"}),
]);

export enum PageInterval {
  SLOW = "slow",
  NORMAL = "normal",
  FAST = "fast",
}

export type PageIntervalMetadata = {
  text: string,
  delay: number,
}

export const PAGE_INTERVAL_KTM = new KeyMetadatas<PageIntervalMetadata>([
  $KTM(PageInterval.SLOW, {text: "Slow", delay: 10000}),
  $KTM(PageInterval.NORMAL, {text: "Normal", delay: 5000}),
  $KTM(PageInterval.FAST, {text: "Fast", delay: 3000}),
]);

@JsonObject()
export class Preferences {

  @JsonProperty()
  pageTransition: PageTransition = PageTransition.SLIDE;

  @JsonProperty()
  pageInterval: PageInterval = PageInterval.NORMAL;
}

export enum TextAlign {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

export type TextAlignMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const TEXT_ALIGN_KTM = new KeyMetadatas<TextAlignMetadata>([
  $KTM(TextAlign.LEFT, {text: "Left", iconType: FormatAlignLeftOutlined}),
  $KTM(TextAlign.CENTER, {text: "Center", iconType: FormatAlignCenterOutlined}),
  $KTM(TextAlign.RIGHT, {text: "Right", iconType: FormatAlignRightOutlined}),
]);

@JsonObject()
export class ImageAttributes {

  @JsonProperty()
  scale: number = 1;
}

@JsonObject()
export class TextAttributes {

  @JsonProperty()
  textAlign: TextAlign = TextAlign.LEFT;

  @JsonProperty()
  fontFamily: string = "Inter";

  @JsonProperty()
  fontSize: number = 14;

  @JsonProperty()
  color: string = "#000";
}

@JsonObject()
export class PageLayoutBlock {

  @JsonProperty()
  mediaId: string;

  @JsonProperty()
  imageAttributes: ImageAttributes = new ImageAttributes();

  @JsonProperty()
  text: string;

  @JsonProperty()
  textAttributes: TextAttributes = new TextAttributes();

  @JsonProperty()
  subtext: string = "Click to edit or delete photo caption.";
}

@JsonObject()
export class PageLayout {

  @JsonProperty()
  readonly id: string;

  @JsonProperty({dataStructure: "dictionary", type: PageLayoutBlock})
  private blocks: { [id: string]: PageLayoutBlock } = {};

  static fromTemplate(template: PageLayoutTemplate): PageLayout {
    return new PageLayout(template.id);
  }

  constructor(id: string) {
    this.id = id;
  }

  private _template: PageLayoutTemplate;

  private template(): PageLayoutTemplate {
    if (!this._template) {
      this._template = PAGE_LAYOUT_TEMPLATES.find(t => t.id === this.id) || PAGE_LAYOUT_TEMPLATES[0];
    }
    return this._template;
  }

  rows(): number {
    return this.template().rows;
  }

  cols(): number {
    return this.template().cols;
  }

  blockSpecs(): PageLayoutBlockSpec[] {
    return this.template().blockSpecs;
  }

  block(index: number): PageLayoutBlock {
    const key = `b${index}`;
    let block = this.blocks[key];
    if (!block) {
      block = new PageLayoutBlock();
      this.blocks[key] = block;
    }
    return block;
  }

  copyBlocks(layout: PageLayout) {
    this.blocks = layout.blocks;
  }
}

@JsonObject()
export class Page {

  @JsonProperty()
  background: string = "#fff";

  @JsonProperty()
  layout: PageLayout = PageLayout.fromTemplate(PAGE_LAYOUT_TEMPLATES[0]);

  setBackground(color: string): Page {
    this.background = color;
    return this;
  }

  setLayoutTemplate(template: PageLayoutTemplate): Page {
    const layout = PageLayout.fromTemplate(template);
    layout.copyBlocks(this.layout);
    this.layout = layout;
    return this;
  }
}

@JsonObject()
export class MediaImage {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly name: string;

  @JsonProperty()
  readonly url: string;

  static createNew(name: string, url: string): MediaImage {
    return new MediaImage(md5(url), name, url);
  }

  constructor(id: string, name: string, url: string) {
    this.id = id;
    this.name = name;
    this.url = url;
  }
}

@JsonObject()
export class Media {

  @JsonProperty()
  readonly images: MediaImage[] = [];
}

@JsonObject()
export class Book {

  @JsonProperty()
  title: string = "My Smile Book";

  @JsonProperty({dataStructure: "array", type: Page})
  readonly pages: Page[] = [];

  @JsonProperty()
  readonly media: Media = new Media();

  @JsonProperty()
  preferences: Preferences = new Preferences();
}

export enum PageElementType {
  IMAGE = "image",
  TEXT = "text",
  SUBTEXT = "subtext",
  BACKGROUND = "background",
}

export type PageElement = {
  page: Page,
  type: PageElementType,
  blockIndex?: number,
}


@JsonObject()
export abstract class BaseVersion extends TypedObject {

  @JsonProperty()
  book: Book;

  constructor(readonly practiceId: string) {
    super(null, null);
  }

  protected objectConfig(): ObjectConfig | null {
    return {
      shared: true,
    };
  }

  protected basePath(): string | null {
    return `practiceData/${this.practiceId}/smilebook`;
  }
}

@JsonObject()
export class DraftVersion extends BaseVersion {

  readonly id: string = "draft";

  static async load(practiceId: string) {
    const obj = new DraftVersion(practiceId);
    await obj.load();
    // obj.book.media.images.push(
    //   MediaImage.createNew("sb_cover.jpg", "/samples/sb_cover.jpg"),
    //   MediaImage.createNew("sb_practice.jpg", "/samples/sb_practice.jpg"),
    //   MediaImage.createNew("1_before.jpg", "/samples/1_before.jpg"),
    //   MediaImage.createNew("1_after.jpg", "/samples/1_after.jpg"),
    //   MediaImage.createNew("manpreet.jpg", "/samples/manpreet.jpg"),
    // );
    //
    return obj;
  }

  protected getType(): Type<any> {
    return DraftVersion;
  }
}

@JsonObject()
export class PublishedVersion extends BaseVersion {

  readonly id: string = "published";

  static async load(practiceId: string) {
    const obj = new PublishedVersion(practiceId);
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return PublishedVersion;
  }
}


export class Smilebook {

  private static instance: Smilebook;

  static getInstance(): Smilebook {
    return this.instance;
  }

  static async loadInstance(practiceId: string): Promise<void> {
    this.instance = new Smilebook(
      practiceId,
      await DraftVersion.load(practiceId),
      await PublishedVersion.load(practiceId),
    );
  }

  private _draft: DraftVersion;

  constructor(
    readonly practiceId: string,
    draft: DraftVersion,
    readonly published: PublishedVersion,
  ) {
    this._draft = draft;
  }

  draft(): DraftVersion {
    if (!this._draft) {
      this._draft = new DraftVersion(this.practiceId);
    }
    return this._draft;
  }

  async save(): Promise<void> {
    await this.draft().save();
  }

  async publish(): Promise<void> {
    this.published.book = this.draft().book;
    await this.published.save();
    await this.draft().delete();
  }
}
